import React, { useState } from 'react';

/**
 * SectionDropdown Component
 *
 * Reusable dropdown for mobile section navigation
 * Matches the AdminPage styling with blue button and dropdown menu
 *
 * @param {Object} props
 * @param {Array} props.sections - Array of section objects: { id, label }
 * @param {string} props.activeSection - Current active section id
 * @param {Function} props.onSectionChange - Callback when section changes
 * @param {Function} props.getSectionLabel - Optional function to get label for active section
 */
const SectionDropdown = ({ sections, activeSection, onSectionChange, getSectionLabel }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const currentLabel = getSectionLabel
    ? getSectionLabel(activeSection)
    : sections.find((s) => s.id === activeSection)?.label || 'Select Section';

  const handleSectionClick = (sectionId) => {
    onSectionChange(sectionId);
    setDropdownOpen(false);
  };

  const styles = {
    container: {
      position: 'relative',
      width: '100%',
      padding: '12px',
      backgroundColor: '#f8f9fa',
    },
    button: {
      width: '100%',
      padding: '15px 18px',
      fontSize: '15px',
      fontWeight: '700',
      color: 'white',
      backgroundColor: '#007bff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '48px',
      boxShadow: '0 2px 8px rgba(0,123,255,0.3)',
      transition: 'all 0.2s',
    },
    chevron: {
      fontSize: '15px',
      transition: 'transform 0.2s',
      flexShrink: 0,
      fontWeight: 'bold',
      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    },
    menu: {
      position: 'absolute',
      top: 'calc(100% - 9px)',
      left: '12px',
      right: '12px',
      backgroundColor: 'white',
      border: '2px solid #007bff',
      borderTop: 'none',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10000,
      overflow: 'hidden',
    },
    menuItem: {
      padding: '15px 18px',
      fontSize: '14px',
      fontWeight: '600',
      backgroundColor: 'white',
      color: '#333',
      border: 'none',
      borderBottom: '1px solid #e0e0e0',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      minHeight: '48px',
      transition: 'background-color 0.2s',
    },
    menuItemActive: {
      backgroundColor: '#007bff',
      color: 'white',
      fontWeight: '700',
    },
  };

  return (
    <div style={styles.container}>
      <button type="button" style={styles.button} onClick={() => setDropdownOpen(!dropdownOpen)}>
        <span>{currentLabel}</span>
        <span style={styles.chevron}>▼</span>
      </button>
      {dropdownOpen && (
        <div style={styles.menu}>
          {sections.map((section) => {
            const isActive = activeSection === section.id || activeSection === section.value;
            return (
              <button
                key={section.id || section.value}
                type="button"
                style={{
                  ...styles.menuItem,
                  ...(isActive ? styles.menuItemActive : {}),
                }}
                onClick={() => handleSectionClick(section.id || section.value)}
              >
                {section.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SectionDropdown;
